import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faPlane, 
    faDownload, 
    faCheck, 
    faSpinner,
    faRefresh,
    faExclamationTriangle 
} from "@fortawesome/free-solid-svg-icons";
import api from "../../api/api";
import "./ivao-tracker.scss";
import {globalVariables} from "../../config";

export function IvaoTracker({ onImportComplete }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [flights, setFlights] = useState([]);
    const [selectedFlights, setSelectedFlights] = useState([]);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalAvailable: 0,
        totalImported: 0
    });

    const fetchFlights = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await api.get('/ivao-tracker/available-flights', {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });
            
            setFlights(response.data.flights || []);
            setStats({
                totalAvailable: response.data.totalAvailable || 0,
                totalImported: response.data.totalImported || 0
            });
        } catch (error) {
            console.error('Failed to fetch IVAO flights:', error);
            setError(t('failed-to-fetch-ivao-flights'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFlights();
    }, []);

    const handleSelectFlight = (flightId) => {
        setSelectedFlights(prev => {
            if (prev.includes(flightId)) {
                return prev.filter(id => id !== flightId);
            } else {
                return [...prev, flightId];
            }
        });
    };

    const handleSelectAll = () => {
        const availableFlights = flights.filter(f => !f.isImported);
        if (selectedFlights.length === availableFlights.length) {
            setSelectedFlights([]);
        } else {
            setSelectedFlights(availableFlights.map(f => f.id));
        }
    };

    const handleImport = async () => {
        if (selectedFlights.length === 0) {
            setError(t('no-flights-selected'));
            return;
        }

        setImporting(true);
        setError(null);

        try {
            // Get the selected flight objects
            const selectedFlightData = flights.filter(f => selectedFlights.includes(f.id));
            
            const response = await api.post('/ivao-tracker/import', 
                { flights: selectedFlightData },
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }}
            );

            if (response.data.success) {
                // Refresh the list
                await fetchFlights();
                setSelectedFlights([]);
                
                // Notify parent component
                if (onImportComplete) {
                    onImportComplete(response.data);
                }
                
                // Show success message
                alert(t('ivao-import-success', { 
                    count: response.data.imported 
                }));
            }
        } catch (error) {
            console.error('Failed to import flights:', error);
            setError(t('failed-to-import-flights'));
        } finally {
            setImporting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '-';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    return (
        <div className="ivao-tracker">
            <div className="tracker-header">
                <h3>
                    <FontAwesomeIcon icon={faPlane} /> {t('ivao-tracker-title')}
                </h3>
                <button 
                    className="btn btn-refresh"
                    onClick={fetchFlights}
                    disabled={loading}
                >
                    <FontAwesomeIcon icon={faRefresh} spin={loading} /> 
                    {t('refresh')}
                </button>
            </div>

            <div className="tracker-stats">
                <div className="stat">
                    <span className="label">{t('available-flights')}:</span>
                    <span className="value">{stats.totalAvailable}</span>
                </div>
                <div className="stat">
                    <span className="label">{t('imported-flights')}:</span>
                    <span className="value">{stats.totalImported}</span>
                </div>
            </div>

            {error && (
                <div className="alert alert-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
                </div>
            )}

            {loading ? (
                <div className="loading">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <p>{t('loading-ivao-flights')}</p>
                </div>
            ) : flights.length === 0 ? (
                <div className="no-flights">
                    <p>{t('no-ivao-flights-found')}</p>
                    <p className="hint">{t("ivao-flights-hint", { icaoCode: globalVariables.OACI })}</p>
                </div>
            ) : (
                <>
                    <div className="flights-table-container">
                        <table className="flights-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input 
                                            type="checkbox"
                                            onChange={handleSelectAll}
                                            checked={selectedFlights.length === flights.filter(f => !f.isImported).length && flights.filter(f => !f.isImported).length > 0}
                                            disabled={flights.filter(f => !f.isImported).length === 0}
                                        />
                                    </th>
                                    <th>{t('date')}</th>
                                    <th>{t('callsign')}</th>
                                    <th>{t('aircraft')}</th>
                                    <th>{t('route')}</th>
                                    <th>{t('duration')}</th>
                                    <th>{t('status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {flights.map(flight => (
                                    <tr key={flight.id} className={flight.isImported ? 'imported' : ''}>
                                        <td>
                                            <input 
                                                type="checkbox"
                                                checked={selectedFlights.includes(flight.id)}
                                                onChange={() => handleSelectFlight(flight.id)}
                                                disabled={flight.isImported}
                                            />
                                        </td>
                                        <td>{formatDate(flight.startTime)}</td>
                                        <td className="callsign">{flight.callsign}</td>
                                        <td>{flight.aircraft}</td>
                                        <td className="route">
                                            {flight.departureIcao} → {flight.arrivalIcao}
                                        </td>
                                        <td>{formatDuration(flight.duration)}</td>
                                        <td>
                                            {flight.isImported ? (
                                                <span className="status imported">
                                                    <FontAwesomeIcon icon={faCheck} /> {t('imported')}
                                                </span>
                                            ) : (
                                                <span className="status available">
                                                    {t('available')}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {selectedFlights.length > 0 && (
                        <div className="import-actions">
                            <p>{t('selected-flights', { count: selectedFlights.length })}</p>
                            <button 
                                className="btn btn-primary"
                                onClick={handleImport}
                                disabled={importing}
                            >
                                <FontAwesomeIcon icon={importing ? faSpinner : faDownload} spin={importing} />
                                {importing ? t('importing') : t('import-selected')}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}